import { capitalize, isEmpty, isNil } from 'lodash';

import { getMetricStr } from 'components/graphs/bar-chart-progress/BarChartProgress';

import { __t, applyTranslations } from 'utils/language';
import { COLORS } from 'utils/reporting';
import { X_AXIS_LENGTH } from '../components/campaign-visibility-chart/CampaignVisibilityChart';
import { abbrNum } from 'utils/helper/common';

import type { BarChartProgressItem, BarChartProgressMetricContainerWidth } from 'types';
import type { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';
import type {
  ReportingTaskTypesRecap,
  ReportingTaskTypesVisibility } from 'modules/reporting/types';

applyTranslations(require('./reporting-task-types.i18n.json'));

export const DEFAULT_VALUE = 'n/a';

export function getNearestRoundNumber(value: number): number {
  if (value % (10 ** (String(value).length - 1)) === 0) {
    return value;
  }

  if (value < 1000) {
    return (Number(String(value)[0]) + 1) * (10 ** (String(value).length - 1));
  }

  const initialTwoDigit = Number(String(value).substring(0, 2));
  const nearestEvenNumber = initialTwoDigit % 2 ? initialTwoDigit + 1 : initialTwoDigit + 2;

  return nearestEvenNumber * (10 ** (String(value).length - 2));
}

export function getChartItems(
  tasktype: ReportingDeliverableTypes,
  recap: ReportingTaskTypesRecap,
  chartType: 'creators' | 'tasks'): Array<BarChartProgressItem> {
  if (isEmpty(recap)) {
    return [];
  }

  const actual = recap[`actual${capitalize(chartType)}`] || 0;
  const agreed = recap[`agreed${capitalize(chartType)}`] || 0;
  const actualPercentage = agreed ? (actual / agreed) * 100 : 0;
  const estimatedPercentage = agreed ? (actual / agreed) * 100 : 0;

  return [
    {
      key: chartType,
      label: __t(`reporting_task_types.${chartType}_label.${tasktype}`),
      actual: {
        percentage: actualPercentage,
        metric: abbrNum(actual, { decimal: 1 }, true)
      },
      estimated: {
        percentage: estimatedPercentage,
        metric: abbrNum(agreed, { decimal: 1 }, true)
      },
      bgColor: COLORS[tasktype].main
    }];
}

export function getVisibilityGraphItems(visibility: ReportingTaskTypesVisibility, tasktype: ReportingDeliverableTypes): Array<BarChartProgressItem> {
  if (isEmpty(visibility)) {
    return [];
  }

  const { agreedFollowers, actualFollowers, totalReach, totalImpressions } = visibility;
  const greatestValue = Math.max(agreedFollowers, totalReach, totalImpressions);
  const nearestRoundValue = getNearestRoundNumber(greatestValue);

  return [
    {
      key: 'followers',
      label: __t('reporting_task_types.visibility.followers_label'),
      width: `${X_AXIS_LENGTH * (agreedFollowers / nearestRoundValue)}%`,
      actual: {
        percentage: (actualFollowers / agreedFollowers) * 100,
        metric: abbrNum(actualFollowers, { decimal: 1 }, true)
      },
      estimated: {
        metric: abbrNum(agreedFollowers, { decimal: 1 }, true)
      },
      bgColor: COLORS[tasktype].main
    },
    {
      key: 'reach',
      label: __t('reporting_task_types.visibility.total_reach_label'),
      width: `${X_AXIS_LENGTH * (totalReach / nearestRoundValue)}%`,
      actual: {
        percentage: 100,
        metric: abbrNum(totalReach, { decimal: 1 }, true)
      },
      estimated: {
        percentage: 100
      },
      bgColor: COLORS[tasktype].main
    },
    {
      key: 'impressions',
      label: __t('reporting_task_types.visibility.total_impressions_label'),
      width: `${X_AXIS_LENGTH * (totalImpressions / nearestRoundValue)}%`,
      actual: {
        percentage: 100,
        metric: abbrNum(totalImpressions, { decimal: 1 }, true)
      },
      estimated: {
        percentage: 100
      },
      bgColor: COLORS[tasktype].main
    }
  ];
}

export function getXAxisSeries(visibility: ReportingTaskTypesVisibility): Array<string> {
  if (isEmpty(visibility)) {
    return [];
  }

  const { agreedFollowers, totalReach, totalImpressions } = visibility;
  const greatestValue = Math.max(agreedFollowers, totalReach, totalImpressions);
  const nearestRoundValue = getNearestRoundNumber(greatestValue);
  const gap = nearestRoundValue / 4;

  return [abbrNum(gap), abbrNum(gap * 2), abbrNum(gap * 3), abbrNum(gap * 4)];
}

export function getMetricTypeWidth(chartItems): BarChartProgressMetricContainerWidth {
  if (isEmpty(chartItems)) {
    return '';
  }

  const maxChar = Math.max(...chartItems.map(item => getMetricStr(item).length));
  switch (true) {
    case maxChar < 4:
      return 'small';
    case maxChar < 8:
      return 'medium';
    case maxChar > 12:
      return 'large';
    default:
      return '';
  }
}

export function getReportingTableValue(value: number | null | undefined, defaultValue?: string): string {
  return !isNil(value) ? `${value}%` : (defaultValue || DEFAULT_VALUE);
}
